import React, { Suspense, useState } from 'react';
import StockTable from '@/containers/stock/search/components/stock-table';
import StockAddModal from '@/containers/stock/maintain/stock-add-modal';
import { IDataModal } from '@/interfaces/dataModal';
import { IStock, ISearchStockReq, ISearchStockRes } from '@/interfaces/stock';
import { Input, TablePaginationConfig } from 'antd';
// import { PlusOutlined } from '@ant-design/icons';
import { searchStocks, uploadCoa } from '@/adapters/stock';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import MediaUploader from '@/components/media/uploader-modal';
import StockMovementModal from '@/components/stock-movement/stock-movement-modal';
import StockReservedModal from '@/components/stock/stock-reserved-modal';
import { IProduct } from '@/interfaces/product';

const _stockModal: IDataModal<IStock> = {
  open: false,
};

const { Search } = Input;

interface IProps {
  data: IProduct;
}

const _criteria: ISearchStockReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  isActive: undefined,
  isDelete: false,
};

const ProductStock = ({ data }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchStockReq>({
    ..._criteria,
    productId: data.id,
    iSample: data.iSample,
    isEquipment: data.isEquipment,
  });
  const [dataModal, setDataModal] = useState<IDataModal<IStock>>(_stockModal);
  const [updateModal, setUploadModal] =
    useState<IDataModal<IStock>>(_stockModal);
  const [movementModal, setMovementModal] =
    useState<IDataModal<IStock>>(_stockModal);
  const [reservedModal, setReservedModal] =
    useState<IDataModal<IStock>>(_stockModal);

  const {
    data: stock,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<ISearchStockRes>, Error>({
    queryKey: ['search-stock', criteria],
    queryFn: async () => await searchStocks(criteria),
  });

  const onActionStock = () => {
    refetch();
    setDataModal({
      ..._stockModal,
    });
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IStock> | SorterResult<IStock>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IStock) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <Search
            className="md:max-w-[400px] md:min-w-[400px]"
            placeholder="ค้นหา"
            allowClear
            size="large"
            enterButton="ค้นหา"
            onSearch={onSearch}
          />
        </div>
        <StockTable
          criteria={criteria}
          stock={stock?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isRefetching}
          onEditData={item =>
            setDataModal({ ..._stockModal, id: item.id, item, open: true })
          }
          onDelete={onActionStock}
          onTableChange={onTableChange}
          onUploadCoa={id => {
            setUploadModal({
              open: true,
              id: id,
            });
          }}
          onShowStockMovement={id => {
            setMovementModal({
              open: true,
              id: id,
            });
          }}
          onShowStockReserved={item => {
            setReservedModal({
              open: true,
              id: item.id,
              item,
            });
          }}
        />
      </div>
      <MediaUploader
        data={{
          modelId: Number(updateModal?.id),
          modelType: 'coa',
        }}
        open={updateModal.open}
        onCancel={() => {
          setUploadModal({
            ..._stockModal,
          });
        }}
        onUploaded={() => {
          setUploadModal({
            ..._stockModal,
          });
          refetch();
        }}
        ExternalApi={data => uploadCoa(data)}
      />
      <StockAddModal
        handleCancel={() => setDataModal({ ..._stockModal })}
        handleOk={() => onActionStock()}
        dataModal={dataModal}
      />
      <Suspense>
        {movementModal.id && (
          <StockMovementModal
            handleCancel={() => setMovementModal({ ..._stockModal })}
            open={movementModal.open}
            stockId={Number(movementModal.id)}
          />
        )}
        {reservedModal.open && reservedModal.item && (
          <StockReservedModal
            handleCancel={() => setReservedModal({ ..._stockModal })}
            open={reservedModal.open}
            stock={reservedModal.item}
          />
        )}
      </Suspense>
    </>
  );
};

export default ProductStock;
